/**
 * Burst-scoped React context exposed by `FloatingActionBurst`.
 *
 * The burst shares these artifacts with every descendant sub-FAB:
 *   - `mainFabRef`: ref to the main FAB element (stable measurement
 *     anchor for burst geometry and related layout).
 *   - `requestBurstClose`: imperative close request used by outside
 *     click / Escape dismissal and other burst-level consumers.
 *   - `registerPanelClose` / `requestCloseOpenPanels`: modal sub-FABs
 *     register a close handler; the main burst FAB invokes
 *     `requestCloseOpenPanels` on short/long press so an open MiniModal
 *     is dismissed before the burst toggles.
 *
 * Isolating the context in its own module lets any component import the
 * hook without pulling in the whole burst implementation.
 */

import { createContext, useContext, type RefObject } from "react";

/** Value exposed by the `FloatingActionBurst` to its descendants. */
export interface FloatingActionBurstContextValue {

    /** Ref of the main FAB button rendered by the burst. */
    "mainFabRef": RefObject<HTMLElement | null>;

    /**
     * Imperatively request the burst to close. Used for outside click /
     * Escape dismissal and other burst-level consumers. Modal sub-FABs
     * intentionally leave the burst open when their panel opens.
     */
    "requestBurstClose": () => void;

    /**
     * Registers a close callback for an open modal sub-FAB panel.
     *
     * @param close Handler that closes the panel (typically `closePanel`).
     * @returns Cleanup function that unregisters the handler.
     */
    "registerPanelClose": ( close: () => void ) => () => void;

    /**
     * Closes every modal sub-FAB panel currently registered. Invoked by
     * the main burst FAB before toggling open/closed state.
     */
    "requestCloseOpenPanels": () => void;
}

/**
 * Shared context instance. `null` indicates the consumer is rendered
 * outside of any `FloatingActionBurst`, in which case it is expected to
 * fall back to standalone behaviour.
 */
export const FloatingActionBurstContext = createContext<FloatingActionBurstContextValue | null>( null );

/**
 * Hook helper used by sub-FABs to read the burst context.
 * Returns `null` when no surrounding burst is found, so consumers can
 * gracefully fall back to their own local ref / ignore the close
 * request.
 *
 * @returns The burst context value, or `null` when rendered standalone.
 */
export const useFloatingActionBurstContext = (): FloatingActionBurstContextValue | null => {
    return useContext( FloatingActionBurstContext );
};
